import { useEffect, useState } from 'react';
import { Loader2, Save, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  PROVIDER_LABELS, STEP_LABELS_DE, OPENAI_QUALITY_OPTIONS,
  modelsForStep, providersForStep,
  type ProviderName,
} from '@/lib/modelCatalog';

interface ToolModelConfigRow {
  id: string;
  tool: 'floorplan' | 'staging';
  step: string;
  provider: ProviderName;
  model: string;
  params: Record<string, unknown>;
  updated_at: string;
}

interface Props { tool: 'floorplan' | 'staging'; }

export const AdminToolModelView: React.FC<Props> = ({ tool }) => {
  const [rows, setRows] = useState<ToolModelConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [justSavedId, setJustSavedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('tool_model_config')
        .select('*')
        .eq('tool', tool)
        .order('step');
      setRows((data ?? []) as ToolModelConfigRow[]);
      setLoading(false);
    })();
  }, [tool]);

  const updateRow = (id: string, patch: Partial<ToolModelConfigRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  const handleSave = async (row: ToolModelConfigRow) => {
    setSavingId(row.id);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('tool_model_config')
      .update({
        provider: row.provider,
        model: row.model,
        params: row.params,
        updated_by: user?.id ?? null,
      })
      .eq('id', row.id);
    setSavingId(null);
    if (error) {
      alert(`Speichern fehlgeschlagen: ${error.message}`);
      return;
    }
    setJustSavedId(row.id);
    setTimeout(() => setJustSavedId(curr => (curr === row.id ? null : curr)), 2500);
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="bg-muted text-sm text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Schritt</th>
              <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Provider</th>
              <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Modell</th>
              <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Parameter</th>
              <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(row => (
              <tr key={row.id} className="hover:bg-muted/30 align-top">
                <td className="px-4 py-3 font-medium whitespace-nowrap">
                  {STEP_LABELS_DE[row.step] ?? row.step}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={row.provider}
                    onChange={e => updateRow(row.id, {
                      provider: e.target.value as ProviderName,
                      // reset to the first model of the step's required kind for the new provider
                      model: modelsForStep(e.target.value as ProviderName, row.step)[0]?.id ?? row.model,
                      params: {},
                    })}
                    className="border rounded px-2 py-1"
                  >
                    {providersForStep(row.step).map(p => (
                      <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={row.model}
                    onChange={e => updateRow(row.id, { model: e.target.value })}
                    className="border rounded px-2 py-1 min-w-[240px]"
                  >
                    {modelsForStep(row.provider, row.step).map(m => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {row.provider === 'openai' ? (
                    <select
                      value={(row.params.quality as string) ?? 'auto'}
                      onChange={e => updateRow(row.id, { params: { ...row.params, quality: e.target.value } })}
                      className="border rounded px-2 py-1"
                    >
                      {OPENAI_QUALITY_OPTIONS.map(q => (
                        <option key={q} value={q}>Quality: {q}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleSave(row)} disabled={savingId === row.id}>
                      {savingId === row.id ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-3 h-3 mr-1" />
                      )}
                      Speichern
                    </Button>
                    {justSavedId === row.id && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 animate-in fade-in">
                        <Check className="w-3 h-3" />
                        Gespeichert
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
